<script lang="ts" setup>
  import { onUnmounted } from 'vue';
  import { useTimer } from '@storefront/storefront';
  import { CartLayout } from '@storefront/components';
  import { useAuthenticated, useViewQuery, useHttpClient, useRoute } from '@othercommerce/foundation';

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  defineProps({
    order: { type: Object, required: true },
    payment: { type: Object, required: true },
    method: { type: Object, required: true },
  });

  const http = useHttpClient();
  const authenticated = useAuthenticated();
  const query = useViewQuery();
  const route = useRoute();

  const { formatted, stopTimer } = useTimer({
    timestamp: query.value?.expires,
    callback: () => {
      http.get(route('web.homepage.index'));
    },
  });

  onUnmounted(() => {
    stopTimer();
  });
</script>
<template>
  <main id="main" class="order">
    <div class="order-failure">
      <SfTile class="order-failure__confirmation">
        <div class="order-failure__icon-wrapper">
          <SfIconDanger aria-hidden="true" />
        </div>
        <main class="order-failure__content">
          <article class="order-failure__header">
            <header class="order-failure__heading-wrapper">
              <h1 class="order-failure__heading">
                {{ $t('SHOP.ORDER.HEADER.PAYMENT_FAILED') }}
              </h1>
            </header>
            <section class="order-failure__message-wrapper">
              <p class="order-failure__message">
                {{ $t('SHOP.ORDER.MESSAGE.PAYMENT_FAILED_EMAIL_NOTICE') }}
              </p>
              <p v-if="authenticated" class="order-failure__account-info">
                {{ $t('SHOP.ORDER.MESSAGE.PAYMENT_FAILED_ACCOUNT_NOTICE') }}
              </p>
            </section>
          </article>

          <article class="order-failure__repay">
            <section class="order-failure__repay-section">
              <p class="order-failure__repay-text">
                {{ $t('SHOP.ORDER.MESSAGE.PAYMENT_FAILED_PAY_AGAIN_NOTICE') }}
              </p>
            </section>
          </article>

          <article class="order-failure__order">
            <section class="order-failure__order-section">
              <p class="order-failure__order-id">
                <span>{{ $t('SHOP.ORDER.LABEL.ORDER_NUMBER') }}</span>
                <strong>{{ order.number }}</strong>
              </p>
            </section>
          </article>

          <nav class="order-failure__navigation">
            <SfButton :href="$route('web.homepage.index')" large>
              {{ $t('SHOP.ACTION.HOMEPAGE') }}
            </SfButton>
          </nav>

          <article class="order-failure__security">
            <p class="order-failure__security-text">
              <span>{{ $t('SHOP.LABEL.PAGE_EXPIRES') }}</span>
              <strong>{{ formatted }}</strong>
            </p>
          </article>
        </main>
      </SfTile>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .icon-danger-marker {
    fill: var(--sf-order-failure-icon-color-marker);
    stroke: var(--sf-order-failure-icon-color-marker);
  }

  .icon-danger-border {
    fill: var(--sf-order-failure-icon-color);
    stroke: var(--sf-order-failure-icon-color);
  }

  .order-failure {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;

    &__confirmation {
      position: relative;
      width: 100%;
      max-width: 700px;
      margin-top: 3rem;
      text-wrap: pretty;
    }

    &__icon-wrapper {
      position: absolute;
      top: 0;
      left: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translate(-50%, -50%);
      height: 100px;
      width: 100px;
      font-size: 3rem;
      color: var(--sf-order-failure-icon-color);
      border-radius: 50%;
    }

    &__content,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__content {
      gap: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }

    &__heading {
      text-align: center;
      margin: 3rem 0 2rem;
    }

    &__order {
      font-size: 1.25rem;
    }

    &__order-id {
      word-break: break-word;
    }

    &__navigation {
      display: flex;
      justify-content: center;
      width: 100%;
      margin: 1rem 0;
    }

    &__security {
      font-size: 0.75rem;
    }
  }

  @include media.query(lg) {
    .order-failure {
      margin: 0;
      padding: 1rem 0;

      &__icon-wrapper {
        width: 70px;
        height: 70px;

        svg {
          font-size: 2rem;
        }
      }

      &__confirmation {
        box-shadow: var(--sf-tile-interactive-box-shadow);
        padding: 1rem 0;
      }

      &__header {
        text-align: center;
        margin-top: 1rem;
      }

      &__order {
        text-align: center;
      }

      &__heading {
        font-size: 1.5rem;
        margin: 1rem 0;
      }
    }

    @include media.query(md) {
      .order-failure__security, .order-failure__repay-text {
        text-align: center;
      }
    }
  }
</style>
